/**
 * CreateCampaignDialog (KAN-595, extracted from ColdCallsView).
 *
 * Owns the create form, the profiles query and the create mutation. After a
 * successful create it navigates to the upload page of the new list.
 *
 * TASK-369: the profile Select renders only when there is a real choice
 * (> 1 profile). The endpoint filters profiles by Organization.mode, so in
 * practice there is one; it is applied silently and shown as a static caption.
 * The users read "Профиль" as "which operator runs this", but it is the bot's
 * ROLE. Do not bring the Select back "for symmetry".
 *
 * KAN-752: a Telegram list belongs to a campaign (an operator of the
 * telegram_user channel). With more than one such operator the campaign is a
 * required choice and is sent as owner_id; with one there is no field and no
 * owner_id (the backend picks it).
 */
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OutcomePreview } from '@/components/datasets/OutcomePreview';
import { CampaignSelect } from '@/components/telegram/CampaignSelect';
import { useChannelOperators } from '@/hooks/useChannelOperators';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { isOperatorRequiredError, OPERATOR_REQUIRED_TEXT } from '@/lib/channelOperators';
import { TELEGRAM_USER_CHANNEL } from '@/services/api/channelOperators';
import { CAMPAIGN_KIND_CHOICES } from '@/lib/campaignKind';
import { datasetsApi } from '@/services/api/datasets';
import type { CampaignKind, CreateDatasetPayload } from '@/types/dataset';

type CreateForm = CreateDatasetPayload & { kind: CampaignKind };

const EMPTY_FORM: CreateForm = { name: '', codename: '', kind: 'voice' };

interface ProfileFieldProps {
  kind: CampaignKind;
  codename: string;
  onChange: (codename: string) => void;
}

/**
 * TASK-369: a control only when the user has something to decide.
 *
 * Branch order is load-bearing: a failed load must not degrade into "нет
 * профилей" (TASK-361), and a pending load must not read as an empty list. In
 * every non-Select branch the form codename stays empty except for the
 * single-profile one, so the submit guard blocks a create with an empty codename
 * that the backend would reject with a bare 400.
 */
function ProfileField({ kind, codename, onChange }: ProfileFieldProps) {
  const isTelegram = kind === 'telegram_outreach';
  const {
    data: profiles,
    isError,
    error,
  } = useQuery({
    queryKey: ['datasetProfiles'],
    queryFn: () => datasetsApi.profiles(),
    staleTime: 5 * 60 * 1000,
  });

  // Single available profile -> pre-selected (TASK-369).
  useEffect(() => {
    if (profiles?.length && !codename) {
      if (profiles.length === 1 || isTelegram) onChange(profiles[0].codename);
    }
  }, [profiles, codename, onChange, isTelegram]);

  // Telegram: neutral texts only (no voice-profile wording). The backend needs
  // a real role codename, so a failed or empty load blocks the create too.
  if (isTelegram) {
    if (isError || profiles?.length === 0) {
      return (
        <div
          data-testid="dataset-profiles-error"
          role="alert"
          className="text-sm text-red-600"
        >
          Не удалось загрузить роль для кампании. Обновите страницу или
          обратитесь к администратору.
        </div>
      );
    }
    if (!profiles) {
      return (
        <div
          data-testid="dataset-profiles-loading"
          className="text-sm text-gray-500"
        >
          Загрузка роли…
        </div>
      );
    }
    return (
      <div
        data-testid="dataset-profile-static"
        className="text-sm text-gray-500"
      >
        Роль:{' '}
        <span className="font-medium text-gray-900">{profiles[0].label}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="dataset-profiles-error"
        role="alert"
        className="text-sm text-red-600"
      >
        Не удалось загрузить профили обзвона:{' '}
        {extractErrorMessage(error, 'неизвестная ошибка')}
      </div>
    );
  }

  if (!profiles) {
    return (
      <div
        data-testid="dataset-profiles-loading"
        className="text-sm text-gray-500"
      >
        Загрузка профилей…
      </div>
    );
  }

  // No support link on purpose: the app has no canonical support contact, and
  // org mode is not something the user can change. Naming the cause gives the
  // words to escalate with.
  if (profiles.length === 0) {
    return (
      <div
        data-testid="dataset-profiles-empty"
        role="alert"
        className="text-sm text-amber-700"
      >
        Для режима этого кабинета не настроено ни одного профиля обзвона -
        создать кампанию нельзя.
      </div>
    );
  }

  if (profiles.length === 1) {
    return (
      <div
        data-testid="dataset-profile-static"
        className="text-sm text-gray-500"
      >
        Роль бота:{' '}
        <span className="font-medium text-gray-900">{profiles[0].label}</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="dataset-codename">Профиль *</Label>
      <Select value={codename} onValueChange={onChange}>
        <SelectTrigger
          id="dataset-codename"
          data-testid="dataset-codename-select"
        >
          <SelectValue placeholder="Выберите профиль" />
        </SelectTrigger>
        <SelectContent>
          {profiles.map((profile) => (
            <SelectItem key={profile.codename} value={profile.codename}>
              {profile.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface CreateCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateCampaignDialog({
  open,
  onOpenChange,
}: CreateCampaignDialogProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [ownerId, setOwnerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isTelegram = form.kind === 'telegram_outreach';
  const campaigns = useChannelOperators(TELEGRAM_USER_CHANNEL, open && isTelegram);
  const campaignRequired = isTelegram && campaigns.choiceRequired;
  // Telegram: until the operators are known (or the campaign is chosen) the
  // owner is unknown, so neither the preview nor the create can be correct.
  const ownerPending =
    isTelegram && (campaigns.isLoading || (campaignRequired && !ownerId));

  // A closed dialog is reset, so every opening starts from a clean form (the
  // profile field pre-selects a lone profile itself once it mounts).
  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setOwnerId('');
      setError(null);
    }
  }, [open]);

  const setCodename = useCallback(
    (codename: string) => setForm((prev) => ({ ...prev, codename })),
    []
  );

  const createMutation = useMutation({
    mutationFn: (payload: CreateDatasetPayload) => datasetsApi.create(payload),
    onSuccess: (dataset) => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      onOpenChange(false);
      setForm(EMPTY_FORM);
      setError(null);
      toast.success('Кампания создана');
      navigate(`/clients/cold-calls/${dataset.id}/upload`);
    },
    onError: (err: unknown) => {
      setError(
        isOperatorRequiredError(err)
          ? OPERATOR_REQUIRED_TEXT
          : extractErrorMessage(err, 'Не удалось создать кампанию')
      );
    },
  });

  const handleSubmit = useCallback(() => {
    if (!form.name.trim()) {
      setError('Название обязательно');
      return;
    }
    if (!form.codename.trim()) {
      setError('Профиль обязателен');
      return;
    }
    if (campaignRequired && !ownerId) {
      setError(OPERATOR_REQUIRED_TEXT);
      return;
    }
    createMutation.mutate(campaignRequired ? { ...form, owner_id: ownerId } : form);
  }, [form, createMutation, campaignRequired, ownerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-dataset-dialog">
        <DialogHeader>
          <DialogTitle>Новая кампания</DialogTitle>
          <DialogDescription>
            Выберите вид списка, создайте кампанию и загрузите базу контактов.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="dataset-name">Название *</Label>
            <Input
              id="dataset-name"
              data-testid="dataset-name-input"
              placeholder="Холодная база Q3"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dataset-kind">Вид *</Label>
            <Select
              value={form.kind}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, kind: value as CampaignKind }))
              }
            >
              <SelectTrigger
                id="dataset-kind"
                data-testid="dataset-kind-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_KIND_CHOICES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {campaignRequired && (
            <CampaignSelect
              operators={campaigns.operators}
              value={ownerId}
              onChange={(id) => {
                setOwnerId(id);
                setError(null);
              }}
              hint="Список уйдёт в рассылку выбранной кампании."
            />
          )}
          {!ownerPending && (
            <OutcomePreview
              kind={form.kind}
              ownerId={campaignRequired ? ownerId : undefined}
            />
          )}
          <ProfileField
            kind={form.kind}
            codename={form.codename}
            onChange={setCodename}
          />
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            data-testid="create-dataset-submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleSubmit}
            disabled={
              createMutation.isPending ||
              !form.codename ||
              ownerPending
            }
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
